import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  OnDestroy,
  signal,
  TemplateRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import {
  DataTableFilter,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { DataProvider } from '@components/abb-table/data-source';
import { PAGE, USER_ACTION } from '@constants/user';
import { getUserLogs, userLogsExport } from '@graphql/user';
import { TRANSLATION_PREFIX, TPipe } from '@pipes/t.pipe';
import { dataProviderFromObservable } from '@utils/data-provider';
import { Apollo } from 'apollo-angular';
import { AbbCommonModule } from 'app/abb-common.module';
import { UserLogsExportQuery, UserLogsExportQueryVariables, GetUserLogsQuery, GetUserLogsQueryVariables, User_Action } from 'gql/graphql';
import { BehaviorSubject, delay, firstValueFrom, Subscription } from 'rxjs';
import { AbbDateComponent } from '../../../../components/abb-date/abb-date.component';
import { TranslateService } from '@ngx-translate/core';

export interface UserLogsDataTable {
  id: string;
  dateTime: Date;
  user: string;
  action: USER_ACTION;
  object: PAGE;
  description: string;
}

type DateTimeInput = {
  date: string | null;
  time: boolean;
};

type UserLogsDataTableFilters = {
  dateTime?: {
    start?: DateTimeInput;
    end?: DateTimeInput;
  };
  user?: string;
  action?: string;
  object?: string;
  description?: string;
};

@Component({
  selector: 'abb-user-logs',
  standalone: true,
  imports: [
    CommonModule,
    AbbCommonModule,
    AbbDateComponent,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    TPipe,
  ],
  template: `
    <abb-table
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [filters]="filters"
      filterTrigger="manual"
      (onManualFiltersApplied)="fetchLogs($event)"
      [defaultFilters]="{}"
      [showPagination]="false"
      [loading]="loading()"
      [dataExport]="true"
      [exporting]="exporting()"
      (onExport)="onExportCSV()"
      [customPaginatorTemplate]="customPaginatorTemplate() ?? null"
    >
      <ng-template #customPaginator>
        <mat-paginator
          [length]="totalItems()"
          [pageSize]="pageSize()"
          [pageIndex]="currentPageIndex()"
          [hidePageSize]="true"
          [className]="'text-abb-font'"
          [disabled]="paginatorDisabled()"
          aria-label="Select page of user logs"
        >
        </mat-paginator>
      </ng-template>
    </abb-table>

    <ng-template #date let-item>
      @if (item.dateTime === null) {
      {{ '-' }}
      } @else {
      <abb-date
        [date]="item.dateTime"
        [showTime]="true"
        [showTooltip]="true"
      ></abb-date>
      }
    </ng-template>
    <ng-template #action let-item>
      {{ getActionLabel(item.action) }}
    </ng-template>
  `,
  styles: ``,
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.user-logs' },
  ],
})
export class UserLogsComponent implements OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  getUserActionOptions(): { value: string, label: string }[] {
    return Object.keys(USER_ACTION).map(value => ({
      value,
      label: this.translate.instant(`settings.tabs.user-logs.action.${value}`)
    }));
  }

  getActionLabel(action: string): string {
    const found = this.getUserActionOptions().find(opt => opt.value === action);
    return found ? found.label : action;
  }

  getUserObjectOptions(): { value: string, label: string }[] {
    return Object.keys(PAGE).map(value => ({
      value,
      label: this.translate.instant(`settings.tabs.user-logs.object.${value}`)
    }));
  }

  getObjectLabel(object: string): string {
    const found = this.getUserObjectOptions().find(opt => opt.value === object);
    return found ? found.label : object;
  }

  @HostBinding('attr.id') componentid = 'users-logs';

  customPaginatorTemplate = viewChild<TemplateRef<unknown>>('customPaginator');
  dateTemplate = viewChild<TemplateRef<unknown>>('date');
  actionTemplate = viewChild<TemplateRef<unknown>>('action');

  private readonly subscriptions: Subscription[] = [];
  private readonly PAGINATION_DELAY_MS = 300;

  cursor = signal<string | null>(null);
  cursorHistory = signal<(string | null)[]>([]);

  pageSize = signal<number>(8);
  loading = signal<boolean>(false);
  hasMore = signal<boolean>(false);
  paginatorDisabled = signal<boolean>(false);
  exporting = signal<boolean>(false);
  totalItems = signal<number>(0);

  userLogs$ = new BehaviorSubject<UserLogsDataTable[] | []>([]);

  filters: DataTableFilter<UserLogsDataTableFilters>[] = [
    { id: 'user', filterType: 'text', label: 'Email', className: 'w-full' },
    {
      id: 'action',
      filterType: 'select',
      filterOptions: [],
      label: 'Action',
      className: 'w-full',
    },
    {
      id: 'object',
      filterType: 'select',
      filterOptions: [],
      label: 'Object',
      className: 'w-full',
    },
    { id: 'description', filterType: 'text', label: 'Description', className: 'w-full' },
    { id: 'dateTime', filterType: 'datetimeRange', label: 'Date', className: 'w-full' },
  ];

  defaultFilters: UserLogsDataTableFilters = {};
  currentFilters: any = this.defaultFilters;

  dataProvider: DataProvider<UserLogsDataTable, {}> =
    dataProviderFromObservable(this.userLogs$, (el, filter) => {
      return true;
    });

  currentPageIndex = computed(() => this.cursorHistory().length);

  async onExportCSV() {
    this.exporting.set(true);

    try {
      const result = await firstValueFrom(
        this.apollo.query<UserLogsExportQuery, UserLogsExportQueryVariables>({
          query: userLogsExport,
          variables: {
            filters: Object.keys(this.currentFilters).length > 0
              ? this.currentFilters
              : undefined,
          },
          fetchPolicy: 'network-only',
        })
      );

      const exportData = result.data.userLogsExport;

      const binaryContent = atob(exportData.content);
      const bytes = new Uint8Array(binaryContent.length);
      for (let i = 0; i < binaryContent.length; i++) {
        bytes[i] = binaryContent.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: exportData.contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting user logs:', error);
    } finally {
      this.exporting.set(false);
    }
  }

  columns = computed<IColumn<UserLogsDataTable>[]>(() => {
    return [
      {
        id: 'id',
        name: 'Id',
        key: 'id',
        hidden: true,
      },
      {
        id: 'dateTime',
        name: 'Date',
        templateRef: this.dateTemplate(),
        translate: false,
      },
      {
        id: 'user',
        name: 'User',
        key: 'user',
        translate: false,
      },
      {
        id: 'action',
        name: 'Action',
        templateRef: this.actionTemplate(),
        translate: true,
      },
      {
        id: 'object',
        name: 'Object',
        key: 'object',
        translate: true,
      },
      {
        id: 'description',
        name: 'Description',
        key: 'description',
        translate: false
      },
    ];
  });

  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService
  ) {
    this.fetchLogs();
  }

  ngOnInit() {
    const actionFilter = this.filters.find(f => f.id === 'action');
    if (actionFilter) {
      actionFilter.filterOptions = this.getUserActionOptions();
    }
    const objectFilter = this.filters.find(f => f.id === 'object');
    if (objectFilter) {
      objectFilter.filterOptions = this.getUserObjectOptions();
    }
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe((event) => {
      const currentIndex = this.currentPageIndex();
      const newIndex = event.pageIndex;

      if (newIndex > currentIndex) {
        this.goToNextPage();
      } else if (newIndex < currentIndex) {
        this.goToPreviousPage();
      }
    });
  }

  fetchLogs(newFilters?: UserLogsDataTableFilters) {
    if (newFilters) {
      const transformedFilters: any = {};

      if (newFilters.user) transformedFilters.user = newFilters.user;
      if (newFilters.action) transformedFilters.action = newFilters.action;
      if (newFilters.object) transformedFilters.object = newFilters.object;
      if (newFilters.description) transformedFilters.description = newFilters.description;

      if (newFilters.dateTime) {
        const { start, end } = newFilters.dateTime;

        if ((start?.date && start.date !== null) || (end?.date && end.date !== null)) {
          transformedFilters.dateTime = {
            start: start?.date ? { date: start.date, time: start.time ?? true } : null,
            end: end?.date ? { date: end.date, time: end.time ?? true } : null
          };
        }
      }

      this.cursor.set(null);
      this.cursorHistory.set([]);
      this.currentFilters = transformedFilters;
    }

    this.loading.set(true);

    this.subscriptions.push(
      this.apollo
        .watchQuery<GetUserLogsQuery, GetUserLogsQueryVariables>({
          query: getUserLogs,
          variables: {
            pageSize: this.pageSize(),
            cursor: this.cursor(),
            filters: Object.keys(this.currentFilters).length > 0 ? this.currentFilters : undefined,
          },
          fetchPolicy: 'network-only',
        })
        .valueChanges.subscribe({
          next: (data) => {
            let userLogs = data.data.getUserLogs.data.map((log) => ({
              id: log.id,
              dateTime: new Date(log.dateTime),
              user: log.user.email,
              action: this.mapGraphQLActionToLocalAction(log.action),
              object: PAGE[log.object],
              description: log.description ?? '',
            }));

            userLogs = userLogs.sort((a, b) => b.dateTime.getTime() - a.dateTime.getTime());

            this.userLogs$.next(userLogs);
            this.hasMore.set(data.data.getUserLogs.hasMore);

        
            const nextCursor = data.data.getUserLogs.nextCursor;
            this.cursor.set(nextCursor ?? null);

      
            if (data.data.getUserLogs.totalItems !== null && data.data.getUserLogs.totalItems !== undefined) {
              this.totalItems.set(data.data.getUserLogs.totalItems);
            }

            this.loading.set(false);
            this.paginatorDisabled.set(false);
          },
          error: (error) => {
            console.error('Error fetching logs:', error);
            this.loading.set(false);
            this.paginatorDisabled.set(false);
          },
        })
    );
  }

  private mapGraphQLActionToLocalAction(graphqlAction: User_Action): USER_ACTION {
    const mappedAction = USER_ACTION[graphqlAction as any as keyof typeof USER_ACTION];
    if (mappedAction !== undefined) {
      return mappedAction;
    }
    return graphqlAction as any as USER_ACTION;
  }

  async goToNextPage() {
    if (!this.hasMore() || this.paginatorDisabled()) return;

    this.paginatorDisabled.set(true);
    this.loading.set(true);

    await firstValueFrom(new BehaviorSubject(0).pipe(delay(this.PAGINATION_DELAY_MS)));

    const cursorForCurrentPage = this.cursorHistory().length === 0
      ? null
      : this.cursorHistory()[this.cursorHistory().length - 1];

    const cursorForNextPage = this.cursor();

    this.cursorHistory.update(history => [...history, cursorForNextPage]);

    this.subscriptions.push(
      this.apollo
        .query<GetUserLogsQuery, GetUserLogsQueryVariables>({
          query: getUserLogs,
          variables: {
            pageSize: this.pageSize(),
            cursor: cursorForNextPage,
            filters: Object.keys(this.currentFilters).length > 0 ? this.currentFilters : undefined,
          },
          fetchPolicy: 'network-only',
        })
        .subscribe({
          next: (data) => {
            const nextCursor = data.data.getUserLogs.nextCursor;
            this.cursor.set(nextCursor ?? null);

            const userLogs = data.data.getUserLogs.data.map((log) => ({
              id: log.id,
              dateTime: new Date(log.dateTime),
              user: log.user.email,
              action: this.mapGraphQLActionToLocalAction(log.action),
              object: PAGE[log.object],
              description: log.description ?? '',
            }));

            this.userLogs$.next(userLogs);
            this.hasMore.set(data.data.getUserLogs.hasMore);

            const totalItems = data.data.getUserLogs.totalItems;
            if (totalItems !== null && totalItems !== undefined) {
              this.totalItems.set(totalItems);
            }

            this.loading.set(false);
            this.paginatorDisabled.set(false);
          },
          error: (error) => {
            console.error('Error fetching next page:', error);
            this.cursorHistory.update(history => history.slice(0, -1));
            this.loading.set(false);
            this.paginatorDisabled.set(false);
          },
        })
    );
  }

  async goToPreviousPage() {
    if (this.cursorHistory().length === 0 || this.paginatorDisabled()) return;

    this.paginatorDisabled.set(true);
    this.loading.set(true);

    await firstValueFrom(new BehaviorSubject(0).pipe(delay(this.PAGINATION_DELAY_MS)));

    const newHistory = [...this.cursorHistory()];
    const poppedCursor = newHistory.pop() ?? null;
    this.cursorHistory.set(newHistory);

    const previousCursor = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;

    this.subscriptions.push(
      this.apollo
        .query<GetUserLogsQuery, GetUserLogsQueryVariables>({
          query: getUserLogs,
          variables: {
            pageSize: this.pageSize(),
            cursor: previousCursor,
            filters: Object.keys(this.currentFilters).length > 0 ? this.currentFilters : undefined,
          },
          fetchPolicy: 'network-only',
        })
        .subscribe({
          next: (data) => {
            const nextCursor = data.data.getUserLogs.nextCursor;
            this.cursor.set(nextCursor ?? null);

            const userLogs = data.data.getUserLogs.data.map((log) => ({
              id: log.id,
              dateTime: new Date(log.dateTime),
              user: log.user.email,
              action: this.mapGraphQLActionToLocalAction(log.action),
              object: PAGE[log.object],
              description: log.description ?? '',
            }));

            this.userLogs$.next(userLogs);
            this.hasMore.set(data.data.getUserLogs.hasMore);

            
            const totalItems = data.data.getUserLogs.totalItems;
            if (totalItems !== null && totalItems !== undefined) {
              this.totalItems.set(totalItems);
            }

            this.loading.set(false);
            this.paginatorDisabled.set(false);
          },
          error: (error) => {
            console.error('Error fetching previous page:', error);
            this.cursorHistory.update(history => [...history, poppedCursor]);
            this.loading.set(false);
            this.paginatorDisabled.set(false);
          },
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
